import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import './AssessmentCard.css';

/** Dollars from display strings like "$5,000" or "+$4,200" */
function parseUsd(s) {
  const m = String(s).match(/[\d,]+/);
  return m ? parseInt(m[0].replace(/,/g, ''), 10) : 0;
}

/** Assessment fee credited toward the pilot invoice (used in net ROI math). */
const ASSESSMENT_CREDIT = 1500;

/**
 * Net after month 1 = revenue recovered (mo. 1) − net pilot cost,
 * where net pilot cost = pilot package − assessment credit applied.
 */
function formatNetAfterMonth1(recoveredStr, pilotPaidStr) {
  const recovered = parseUsd(recoveredStr);
  const pilot = parseUsd(pilotPaidStr);
  const netPilot = pilot - ASSESSMENT_CREDIT;
  const net = recovered - netPilot;
  if (net === 0) return '$0';
  const sign = net < 0 ? '−' : '+';
  return `${sign}$${Math.abs(net).toLocaleString()}`;
}

const ASSESSMENT_DATA = {
  medspa: {
    leaks: [
      { n: 'Missed calls — no recovery system', v: '~$3,800/mo' },
      { n: 'Lapsed clients never contacted', v: '~$2,100/mo' },
      { n: 'No-shows with no reminder sequence', v: '~$1,400/mo' },
    ],
    total: '~$7,300/mo',
    fixes: [
      { n: 'Missed call SMS recovery', t: '3-day build' },
      { n: 'Lapsed client reactivation campaign', t: '5-day build' },
    ],
    metrics: [
      { v: '↓ 38%', l: 'No-show rate', cls: 'green' },
      { v: '+$6,100', l: 'Recovered mo. 1', cls: 'green' },
      { v: '58 sec', l: 'Avg response time', cls: 'gold' },
    ],
    roi: {
      pilotPaid: '$5,000',
      recoveredMo1: '+$6,100',
    },
    floats: [
      { i: '✓', ti: 'Lead recovered', su: 'Missed call → booked in 4 min' },
      { i: '✓', ti: 'No-show prevented', su: 'Reminder sent · client confirmed' },
      { i: '✓', ti: 'Client reactivated', su: '14 months lapsed → booked today' },
    ],
  },
  insurance: {
    leaks: [
      { n: 'Quotes not followed up < 24hrs', v: '~$4,200/mo' },
      { n: 'Renewals managed manually', v: '~$2,800/mo in lapses' },
      { n: 'After-hours leads unanswered', v: '~$1,800/mo' },
    ],
    total: '~$8,800/mo',
    fixes: [
      { n: '5-touch quote follow-up sequence', t: '4-day build' },
      { n: 'Automated renewal reminder sequence', t: '3-day build' },
    ],
    metrics: [
      { v: '↓ 23%', l: 'Policy lapse rate', cls: 'green' },
      { v: '+$7,400', l: 'Recovered mo. 1', cls: 'green' },
      { v: '5 touches', l: 'Per cold lead', cls: 'gold' },
    ],
    roi: {
      pilotPaid: '$5,000',
      recoveredMo1: '+$7,400',
    },
    floats: [
      { i: '✓', ti: 'Quote followed up', su: 'Day 4 touch → client responded' },
      { i: '✓', ti: 'Renewal retained', su: '60-day reminder sent automatically' },
      { i: '✓', ti: 'After-hours captured', su: 'Form at 9pm → replied in 2 min' },
    ],
  },
  vet: {
    leaks: [
      { n: 'Missed calls during appointments', v: '~$2,600/mo' },
      { n: 'Annual checkups not triggered', v: '~230 lapsed patients' },
      { n: 'Manual appointment reminders', v: '~6 hrs/week wasted' },
    ],
    total: '~$4,800/mo',
    fixes: [
      { n: 'Missed call recovery + instant SMS', t: '2-day build' },
      { n: 'Annual checkup reactivation flow', t: '4-day build' },
    ],
    metrics: [
      { v: '↓ 31%', l: 'No-show rate', cls: 'green' },
      { v: '+$4,200', l: 'Recovered mo. 1', cls: 'green' },
      { v: '6 hrs', l: 'Admin saved/week', cls: 'gold' },
    ],
    roi: {
      pilotPaid: '$4,500',
      recoveredMo1: '+$4,200',
    },
    floats: [
      { i: '✓', ti: 'Missed call recovered', su: 'SMS in 58 sec · appointment booked' },
      { i: '✓', ti: 'Checkup reminder sent', su: 'Max overdue — owner booked same day' },
      { i: '✓', ti: 'Review received', su: 'Post-visit request → 5 stars Google' },
    ],
  },
  law: {
    leaks: [
      { n: 'Intake not responded < 1 hour', v: '~$8,500/mo in missed cases' },
      { n: 'Consultations not followed up', v: '3–4 cases/month cold' },
      { n: 'Document chasing — manual', v: '~14 hrs/week wasted' },
    ],
    total: '~$11,200/mo',
    fixes: [
      { n: 'Instant intake response + routing', t: '5-day build' },
      { n: 'Consultation follow-up sequence', t: '3-day build' },
    ],
    metrics: [
      { v: '2× faster', l: 'Intake response', cls: 'green' },
      { v: '+$9,200', l: 'Recovered mo. 1', cls: 'green' },
      { v: '14 hrs', l: 'Staff time freed', cls: 'gold' },
    ],
    roi: {
      pilotPaid: '$6,000',
      recoveredMo1: '+$9,200',
    },
    floats: [
      { i: '✓', ti: 'Intake captured', su: '10:47pm inquiry → responded in 90 sec' },
      { i: '✓', ti: 'Lead re-engaged', su: 'Day 5 follow-up → consultation booked' },
      { i: '✓', ti: 'Docs collected', su: 'Auto-reminder → file complete in 3 days' },
    ],
  },
};

const TABS = [
  { key: 'medspa', label: 'Medspa' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'vet', label: 'Vet Clinic' },
  { key: 'law', label: 'Law Firm' },
];

function LeakWarningIcon() {
  return (
    <svg viewBox="0 0 9 9" fill="none" aria-hidden="true">
      <circle cx="4.5" cy="4.5" r="3.8" stroke="rgba(226,75,74,0.8)" strokeWidth="1.1" />
      <line x1="4.5" y1="2.8" x2="4.5" y2="5.2" stroke="rgba(226,75,74,0.8)" strokeWidth="1.1" strokeLinecap="round" />
      <circle cx="4.5" cy="6.4" r="0.55" fill="rgba(226,75,74,0.8)" />
    </svg>
  );
}

function FixCheckIcon() {
  return (
    <svg viewBox="0 0 7 7" fill="none" aria-hidden="true">
      <polyline
        points="1.2,3.5 3,5.2 5.8,1.5"
        stroke="white"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function runCountAnimation(setter, target, duration = 800) {
  const match = target.match(/(\d[\d,]*)/);
  if (!match) {
    setter(target);
    return () => {};
  }
  const end = parseInt(match[1].replace(/,/g, ''), 10);
  const startIdx = match.index;
  const prefix = target.slice(0, startIdx);
  const suffix = target.slice(startIdx + match[1].length);
  const steps = 30;
  let step = 0;
  const id = window.setInterval(() => {
    step += 1;
    const val = Math.round(end * (step / steps));
    setter(`${prefix}${val.toLocaleString()}${suffix}`);
    if (step >= steps) {
      window.clearInterval(id);
      setter(target);
    }
  }, duration / steps);
  return () => window.clearInterval(id);
}

const AssessmentCard = () => {
  const outcomesHeadingId = useId();
  const outcomesRegionId = useId();

  const [activeKey, setActiveKey] = useState('medspa');
  const skipScanRef = useRef(true);

  const [scanWrapActive, setScanWrapActive] = useState(false);
  const [cardScanning, setCardScanning] = useState(false);
  const [scanLineKey, setScanLineKey] = useState(0);

  const [leakShown, setLeakShown] = useState(() => ASSESSMENT_DATA.medspa.leaks.map(() => false));
  const [fixShown, setFixShown] = useState(() => ASSESSMENT_DATA.medspa.fixes.map(() => false));
  const [totalShown, setTotalShown] = useState(false);
  const [outcomesExpanded, setOutcomesExpanded] = useState(false);
  const [roiShown, setRoiShown] = useState(false);

  const [totalText, setTotalText] = useState(ASSESSMENT_DATA.medspa.total);
  const [roiNetText, setRoiNetText] = useState(() =>
    formatNetAfterMonth1(
      ASSESSMENT_DATA.medspa.roi.recoveredMo1,
      ASSESSMENT_DATA.medspa.roi.pilotPaid
    )
  );

  const [floatOn, setFloatOn] = useState(false);
  const [floatBarGo, setFloatBarGo] = useState(false);
  const [floatIndex, setFloatIndex] = useState(0);
  const skipFloatTransition = useRef(true);

  const data = ASSESSMENT_DATA[activeKey];
  const float = data.floats[floatIndex % data.floats.length];
  const economics = useMemo(() => {
    const pilot = parseUsd(data.roi.pilotPaid);
    const recovered = parseUsd(data.roi.recoveredMo1);
    const netPilot = pilot - ASSESSMENT_CREDIT;
    const netM1 = recovered - netPilot;
    return {
      netM1,
      netMonth1Display: formatNetAfterMonth1(data.roi.recoveredMo1, data.roi.pilotPaid),
    };
  }, [data.roi]);

  const roiNetTarget = economics.netMonth1Display;
  const roiNetIsNegative = economics.netM1 < 0;

  const triggerScan = useCallback(() => {
    setScanLineKey((k) => k + 1);
    setScanWrapActive(true);
    setCardScanning(true);
    window.setTimeout(() => {
      setScanWrapActive(false);
      setCardScanning(false);
    }, 1800);
  }, []);

  useEffect(() => {
    const t1 = window.setTimeout(() => setFloatOn(true), 900);
    const t2 = window.setTimeout(() => setFloatBarGo(true), 1000);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setFloatIndex((i) => (i + 1) % ASSESSMENT_DATA[activeKey].floats.length);
    }, 4500);
    return () => window.clearInterval(id);
  }, [activeKey]);

  useEffect(() => {
    if (skipFloatTransition.current) {
      skipFloatTransition.current = false;
      return undefined;
    }
    setFloatOn(false);
    setFloatBarGo(false);
    let innerTimer;
    const outerTimer = window.setTimeout(() => {
      setFloatOn(true);
      innerTimer = window.setTimeout(() => setFloatBarGo(true), 100);
    }, 400);
    return () => {
      window.clearTimeout(outerTimer);
      if (innerTimer) window.clearTimeout(innerTimer);
    };
  }, [floatIndex, activeKey]);

  useEffect(() => {
    const skipScan = skipScanRef.current;
    skipScanRef.current = false;

    const timers = [];
    const run = (fn, delay) => {
      timers.push(window.setTimeout(fn, delay));
    };

    setLeakShown(data.leaks.map(() => false));
    setFixShown(data.fixes.map(() => false));
    setTotalShown(false);
    setOutcomesExpanded(false);
    setRoiShown(false);
    setTotalText(data.total);
    setRoiNetText(roiNetTarget);

    if (!skipScan) triggerScan();

    data.leaks.forEach((_, i) => {
      run(() => {
        setLeakShown((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, skipScan ? i * 100 : 400 + i * 130);
    });

    const td = skipScan ? data.leaks.length * 100 + 80 : 400 + data.leaks.length * 130 + 100;
    run(() => {
      setTotalShown(true);
      run(() => {
        const cancel = runCountAnimation(setTotalText, data.total);
        timers.push(cancel);
      }, 100);
    }, td);

    const fd = td + 200;
    data.fixes.forEach((_, i) => {
      run(() => {
        setFixShown((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, fd + i * 160);
    });

    return () => {
      timers.forEach((t) => {
        if (typeof t === 'function') t();
        else window.clearTimeout(t);
      });
    };
  }, [activeKey, data, triggerScan, roiNetTarget]);

  useEffect(() => {
    if (!outcomesExpanded) {
      setRoiShown(false);
      return undefined;
    }

    const timers = [];
    setRoiShown(false);
    const netStartsNegative = economics.netM1 < 0;
    setRoiNetText(netStartsNegative ? '−$0' : '+$0');

    timers.push(
      window.setTimeout(() => {
        setRoiShown(true);
      }, 150)
    );
    timers.push(
      window.setTimeout(() => {
        timers.push(runCountAnimation(setRoiNetText, roiNetTarget));
      }, 320)
    );

    return () => {
      timers.forEach((t) => {
        if (typeof t === 'function') t();
        else window.clearTimeout(t);
      });
    };
  }, [outcomesExpanded, data.roi, roiNetTarget, activeKey, economics.netM1]);

  const onTabClick = (key) => {
    if (key === activeKey) return;
    setActiveKey(key);
    setFloatIndex(0);
  };

  return (
    <div className="c8">
      <div className={`c8-float ${floatOn ? 'on' : ''}`}>
        <div className="c8-float-icon">{float.i}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="c8-float-title">{float.ti}</div>
          <div className="c8-float-sub">{float.su}</div>
          <div className="c8-float-bar">
            <div className={`c8-float-fill ${floatBarGo ? 'go' : ''}`} />
          </div>
        </div>
      </div>

      <div className={`c8-scan-wrap ${scanWrapActive ? 'active' : ''}`} aria-hidden="true">
        <div key={scanLineKey} className="c8-scan-line" />
      </div>

      <div className={`c8-card ${cardScanning ? 'scanning' : ''}`}>
        <div className="c8-hdr">
          <div className="c8-dots" aria-hidden="true">
            <div className="c8-dot" />
            <div className="c8-dot" />
            <div className="c8-dot" />
          </div>
          <div className="c8-hdr-title">Operations Assessment Report</div>
          <div className="c8-live">
            <div className="c8-live-dot" />
            LIVE IN 7–14 DAYS
          </div>
        </div>

        <div className="c8-tabs" role="tablist" aria-label="Sample industries">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeKey === tab.key}
              className={`c8-tab ${activeKey === tab.key ? 'active' : ''}`}
              onClick={() => onTabClick(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="c8-lbl">
          <span>Revenue leaks found</span>
          <span className="c8-lbl-badge">{data.leaks.length} gaps</span>
        </div>
        <div>
          {data.leaks.map((leak, i) => (
            <div key={leak.n} className={`c8-leak ${leakShown[i] ? 'show' : ''}`}>
              <div className="c8-leak-ico">
                <LeakWarningIcon />
              </div>
              <div>
                <div className="c8-leak-name">{leak.n}</div>
                <div className="c8-leak-val">{leak.v}</div>
              </div>
            </div>
          ))}
        </div>

        <div className={`c8-total ${totalShown ? 'show' : ''}`}>
          <span className="c8-total-label">Est. monthly leakage</span>
          <span className="c8-total-val">{totalText}</span>
        </div>

        <div className="c8-div" aria-hidden="true" />

        <div className="c8-lbl">
          <span>Recommended fixes</span>
          <span className="c8-lbl-badge green">{data.fixes.length} builds</span>
        </div>
        <div>
          {data.fixes.map((fix, i) => (
            <div key={fix.n} className={`c8-fix ${fixShown[i] ? 'show' : ''}`}>
              <div className="c8-fix-chk">
                <FixCheckIcon />
              </div>
              <div>
                <div className="c8-fix-name">{fix.n}</div>
                <div className="c8-fix-time">{fix.t}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="c8-div" aria-hidden="true" />

        <button
          type="button"
          className="c8-outcomes-toggle"
          aria-expanded={outcomesExpanded}
          aria-controls={outcomesRegionId}
          id={outcomesHeadingId}
          onClick={() => setOutcomesExpanded((e) => !e)}
        >
          <span className="c8-outcomes-toggle-label">View outcomes &amp; ROI</span>
          <ChevronDown className="c8-outcomes-chevron" size={18} strokeWidth={2} aria-hidden="true" />
        </button>
        <div
          id={outcomesRegionId}
          role="region"
          aria-labelledby={outcomesHeadingId}
          aria-hidden={!outcomesExpanded}
          className={`c8-outcomes-wrap ${outcomesExpanded ? 'is-open' : ''}`}
        >
          <div className="c8-outcomes-inner">
            <div className="c8-lbl c8-lbl--in-panel">
              <span>Performance after Cure8</span>
            </div>
            <div className={`c8-metrics ${outcomesExpanded ? 'show' : ''}`}>
              {data.metrics.map((m) => (
                <div key={m.l} className="c8-metric">
                  <div className={`c8-metric-val ${m.cls}`}>{m.v}</div>
                  <div className="c8-metric-lbl">{m.l}</div>
                </div>
              ))}
            </div>

            <div className="c8-div" aria-hidden="true" />

            <div className={`c8-roi c8-roi--simple ${roiShown ? 'show' : ''}`}>
              <p className="c8-roi-simple-label">Month 1 net ROI (sample)</p>
              <p
                className={`c8-roi-simple-val ${roiNetIsNegative ? 'is-neg' : 'is-pos'}`}
              >
                {roiNetText}
              </p>
            </div>

            <div className="c8-div" aria-hidden="true" />

            <p className="c8-foot-line">
              <strong>Pro</strong> Plan · Sample
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentCard;
